import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Skeleton,
  Divider,
} from "@mui/material";
import { Business, Person, CalendarToday, Tag, TableChart, CheckCircle, Cancel, ContentCopy } from "@mui/icons-material";
import useApiNew from "../../hooks/useApi";
import { apifilterResultsSummary, method } from "../../apiPath";
import { useSelector } from "react-redux";
import keyNames from "../../keyName";

const TEAL = "#0D9A8F";
const TEAL_DARK = "#0A7A72";
const TEAL_LIGHT = "#e6f7f6";

// ── Skeleton shown while the API is loading ──────────────────────────────────
const PageSkeleton = () => (
  <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
    {/* Top bar skeleton */}
    <Box sx={{ bgcolor: TEAL, px: { xs: 2, md: 5 }, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Skeleton variant="rounded" width={260} height={28} sx={{ bgcolor: "rgba(255,255,255,0.25)", borderRadius: 1 }} />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Skeleton variant="rounded" width={90} height={24} sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: 4 }} />
        <Skeleton variant="rounded" width={110} height={24} sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: 4 }} />
      </Box>
    </Box>

    <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
      {/* Stat cards skeleton */}
      <Grid container spacing={3} mb={4}>
        {[1, 2].map((i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Skeleton variant="rounded" height={110} sx={{ borderRadius: 3 }} />
          </Grid>
        ))}
      </Grid>

      {/* Table section skeleton */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="rounded" width={220} height={22} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 4, ml: "auto" }} />
      </Box>
      <Skeleton variant="rounded" height={46} sx={{ mb: 0.5, borderRadius: 1 }} />
      {[1, 2, 3, 4, 5, 6, 7].map((i) => (
        <Skeleton key={i} variant="rounded" height={38} sx={{ mb: 0.5, borderRadius: 1, opacity: 1 - i * 0.08 }} />
      ))}
    </Box>
  </Box>
);

const ShareStatsPage = () => {
  const { filterId } = useParams();
  const hitApi = useApiNew();

  const filterResultsSummary = useSelector(
    (state) => state.mainReducer.filterResultsSummary
  );
  const isLoading = useSelector((state) => state.mainReducer.isLoading);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (filterId) {
      hitApi(
        `${apifilterResultsSummary}/${filterId}`,
        null,
        method.get,
        keyNames.filterResultsSummary
      );
    }
  }, [filterId, hitApi]);

  if (isLoading === keyNames.filterResultsSummary) {
    return <PageSkeleton />;
  }

  const storeData = filterResultsSummary?.data;
  const filterDataSummary =
    storeData?.campaignFilterId?.filteredData?.summary || {};
  const expiresAt = storeData?.expiresAt;
  const revisionNo = storeData?.revisionNo;

  const matchStats = storeData?.matchStats || null;

  const crossTabResult = storeData?.crossTabData || {};
  const isNested = crossTabResult?.nested === true;

  // Flat
  const industries = crossTabResult?.rows || [];
  const seniorities = crossTabResult?.cols || [];
  const crossTabData = crossTabResult?.crossTab || {};
  const industryTotals = crossTabResult?.rowTotals || {};
  const seniorityTotals = crossTabResult?.colTotals || {};

  // Nested
  const outerCols = crossTabResult?.outerCols || [];
  const innerCols = crossTabResult?.innerCols || [];
  const outerInnerTotals = crossTabResult?.outerInnerTotals || {};
  const nestedRowTotals = crossTabResult?.rowTotals || {};

  const hasTable = industries.length > 0 || (isNested && outerCols.length > 0);

  const rowFieldLabel = crossTabResult.rowField || "Industry";
  const colFieldLabel = crossTabResult.colField
    ? crossTabResult.colField.replace("+", " × ")
    : "Seniority";

  const stickyCell = {
    position: "sticky",
    left: 0,
    zIndex: 2,
    bgcolor: "#fff",
    borderRight: "2px solid",
    borderColor: "divider",
    minWidth: 180,
    maxWidth: 220,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const headerStickyCell = {
    ...stickyCell,
    bgcolor: TEAL_DARK,
    color: "#fff",
    zIndex: 3,
    borderRight: "2px solid rgba(255,255,255,0.3)",
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>

      {/* ── Top bar ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
          px: { xs: 2, md: 5 },
          py: { xs: 2, md: 2.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          boxShadow: "0 2px 12px rgba(13,154,143,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <TableChart sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#fff" lineHeight={1.2} letterSpacing={0.3}>
              Kestone
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.75)" lineHeight={1}>
              Campaign Audience Summary
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {revisionNo != null && (
            <Chip
              icon={<Tag sx={{ fontSize: 12, color: "#fff !important" }} />}
              label={`Revision ${revisionNo}`}
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600, fontSize: 11, border: "1px solid rgba(255,255,255,0.25)", backdropFilter: "blur(4px)" }}
            />
          )}
          {expiresAt && (
            <Chip
              icon={<CalendarToday sx={{ fontSize: 12, color: "#fff !important" }} />}
              label={`Expires ${new Date(expiresAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`}
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600, fontSize: 11, border: "1px solid rgba(255,255,255,0.25)", backdropFilter: "blur(4px)" }}
            />
          )}
        </Box>
      </Box>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 5 }, py: { xs: 3, md: 4 }, maxWidth: 1400, mx: "auto" }}>

        {/* ── Stat cards ──────────────────────────────────────────────── */}
        <Grid container spacing={3} mb={4}>
          {/* Unique Companies */}
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1.5px solid",
                borderColor: `${TEAL}30`,
                background: `linear-gradient(135deg, ${TEAL_LIGHT} 0%, #fff 60%)`,
                transition: "box-shadow 0.2s, transform 0.15s",
                "&:hover": { boxShadow: `0 8px 24px ${TEAL}22`, transform: "translateY(-2px)" },
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2.5, py: 3, px: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "16px",
                    background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: `0 4px 14px ${TEAL}50`,
                  }}
                >
                  <Business sx={{ fontSize: 28, color: "#fff" }} />
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight={800} lineHeight={1} color={TEAL_DARK} sx={{ letterSpacing: -1 }}>
                    {(matchStats != null ? (matchStats.withData ?? 0) : (filterDataSummary.uniqueCompanies ?? 0)).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>
                    Companies with Data
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Contacts */}
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1.5px solid #7c3aed30",
                background: "linear-gradient(135deg, #f5f3ff 0%, #fff 60%)",
                transition: "box-shadow 0.2s, transform 0.15s",
                "&:hover": { boxShadow: "0 8px 24px #7c3aed22", transform: "translateY(-2px)" },
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2.5, py: 3, px: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 4px 14px #7c3aed50",
                  }}
                >
                  <Person sx={{ fontSize: 28, color: "#fff" }} />
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight={800} lineHeight={1} color="#5b21b6" sx={{ letterSpacing: -1 }}>
                    {(filterDataSummary.totalContacts || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>
                    Total Contacts
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Client match stat cards ─────────────────────────────────── */}
        {matchStats && (
          <Grid container spacing={3} mb={4}>
            {/* Not Matched — commented out
            <Grid item xs={12} sm={4}>
              <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid #ea580c30", background: "linear-gradient(135deg, #fff7ed 0%, #fff 60%)", transition: "box-shadow 0.2s, transform 0.15s", "&:hover": { boxShadow: "0 8px 24px #ea580c22", transform: "translateY(-2px)" } }}>
                <CardContent sx={{ display: "flex", alignItems: "center", gap: 2.5, py: 3, px: 3 }}>
                  <Box sx={{ width: 60, height: 60, borderRadius: "16px", background: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 14px #ea580c50" }}><Cancel sx={{ fontSize: 28, color: "#fff" }} /></Box>
                  <Box><Typography variant="h3" fontWeight={800} lineHeight={1} color="#c2410c" sx={{ letterSpacing: -1 }}>{(matchStats.notMatched || 0).toLocaleString()}</Typography><Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>Not Matched</Typography></Box>
                </CardContent>
              </Card>
            </Grid>
            */}

            {/* Duplicates — commented out
            <Grid item xs={12} sm={4}>
              <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid #2563eb30", background: "linear-gradient(135deg, #eff6ff 0%, #fff 60%)", transition: "box-shadow 0.2s, transform 0.15s", "&:hover": { boxShadow: "0 8px 24px #2563eb22", transform: "translateY(-2px)" } }}>
                <CardContent sx={{ display: "flex", alignItems: "center", gap: 2.5, py: 3, px: 3 }}>
                  <Box sx={{ width: 60, height: 60, borderRadius: "16px", background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 14px #2563eb50" }}><ContentCopy sx={{ fontSize: 28, color: "#fff" }} /></Box>
                  <Box><Typography variant="h3" fontWeight={800} lineHeight={1} color="#1d4ed8" sx={{ letterSpacing: -1 }}>{(matchStats.duplicates || 0).toLocaleString()}</Typography><Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>Duplicates</Typography></Box>
                </CardContent>
              </Card>
            </Grid>
            */}
          </Grid>
        )}

        {/* ── Cross-tab table ──────────────────────────────────────────── */}
        {hasTable && (
          <Box>
            {/* Table header row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    bgcolor: TEAL_LIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TableChart sx={{ fontSize: 16, color: TEAL }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                  Distribution Table
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                <Chip label={rowFieldLabel} size="small" sx={{ bgcolor: TEAL_LIGHT, color: TEAL_DARK, fontWeight: 600, fontSize: 11 }} />
                <Typography variant="caption" color="text.disabled" fontWeight={600}>×</Typography>
                <Chip label={colFieldLabel} size="small" sx={{ bgcolor: "#f5f3ff", color: "#5b21b6", fontWeight: 600, fontSize: 11 }} />
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                width: "100%",
                boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
              }}
            >
              <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                <Table size="small" sx={{ tableLayout: "auto", minWidth: 600 }}>

                  {/* ── FLAT header ─────────────────────────────────────── */}
                  {!isNested && (
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerStickyCell}>{rowFieldLabel}</TableCell>
                        {seniorities.map((sen) => (
                          <TableCell key={sen} align="center" sx={{ fontWeight: 700, bgcolor: TEAL, color: "#fff", fontSize: 11, whiteSpace: "nowrap", px: 1.5, py: 1 }}>
                            {sen || "-"}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: TEAL_DARK, color: "#fff", fontSize: 11, whiteSpace: "nowrap", px: 1.5, py: 1 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                  )}

                  {/* ── NESTED header ───────────────────────────────────── */}
                  {isNested && (
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headerStickyCell }} rowSpan={2}>{rowFieldLabel}</TableCell>
                        {outerCols.map((o) => (
                          <TableCell key={o} align="center" colSpan={innerCols.length} sx={{ fontWeight: 700, bgcolor: TEAL, color: "#fff", fontSize: 11, whiteSpace: "nowrap", px: 1.5, py: 0.75, borderRight: "1px solid rgba(255,255,255,0.3)" }}>
                            {o || "-"}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: TEAL_DARK, color: "#fff", fontSize: 11, whiteSpace: "nowrap", px: 1.5, py: 0.75 }} rowSpan={2}>Total</TableCell>
                      </TableRow>
                      <TableRow>
                        {outerCols.map((o) =>
                          innerCols.map((i) => (
                            <TableCell key={`${o}_${i}`} align="center" sx={{ fontWeight: 600, bgcolor: "#0c8a80", color: "#fff", fontSize: 10, whiteSpace: "nowrap", px: 1, py: 0.5 }}>
                              {i || "-"}
                            </TableCell>
                          ))
                        )}
                      </TableRow>
                    </TableHead>
                  )}

                  <TableBody>
                    {/* ── FLAT rows ─────────────────────────────────────── */}
                    {!isNested && industries.map((ind, idx) => (
                      <TableRow key={ind} sx={{ bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa", "&:hover": { bgcolor: TEAL_LIGHT } }}>
                        <TableCell sx={{ ...stickyCell, bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa", fontWeight: 500, fontSize: 12, py: 0.75 }} title={ind || "-"}>{ind || "-"}</TableCell>
                        {seniorities.map((sen) => (
                          <TableCell key={sen} align="center" sx={{ fontSize: 12, px: 1.5, py: 0.75, whiteSpace: "nowrap" }}>
                            {(crossTabData[ind]?.[sen] || 0).toLocaleString()}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 12, px: 1.5, py: 0.75, bgcolor: TEAL_LIGHT, color: TEAL_DARK, whiteSpace: "nowrap" }}>
                          {(industryTotals[ind] || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* ── FLAT grand total ──────────────────────────────── */}
                    {!isNested && (
                      <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                        <TableCell sx={{ ...stickyCell, bgcolor: "#f1f5f9", fontWeight: 700, fontSize: 12, py: 1 }}>Grand Total</TableCell>
                        {seniorities.map((sen) => (
                          <TableCell key={sen} align="center" sx={{ fontWeight: 700, fontSize: 12, px: 1.5, py: 1, whiteSpace: "nowrap" }}>
                            {(seniorityTotals[sen] || 0).toLocaleString()}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 800, fontSize: 13, px: 1.5, py: 1, bgcolor: TEAL, color: "#fff", whiteSpace: "nowrap" }}>
                          {(filterDataSummary.totalContacts || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* ── NESTED rows ───────────────────────────────────── */}
                    {isNested && industries.map((ind, idx) => (
                      <TableRow key={ind} sx={{ bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa", "&:hover": { bgcolor: TEAL_LIGHT } }}>
                        <TableCell sx={{ ...stickyCell, bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa", fontWeight: 500, fontSize: 12, py: 0.75 }} title={ind || "-"}>{ind || "-"}</TableCell>
                        {outerCols.map((o) =>
                          innerCols.map((i) => (
                            <TableCell key={`${o}_${i}`} align="center" sx={{ fontSize: 12, px: 1, py: 0.75, whiteSpace: "nowrap" }}>
                              {(crossTabData[ind]?.[o]?.[i] || 0).toLocaleString()}
                            </TableCell>
                          ))
                        )}
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 12, px: 1.5, py: 0.75, bgcolor: TEAL_LIGHT, color: TEAL_DARK, whiteSpace: "nowrap" }}>
                          {(nestedRowTotals[ind] || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* ── NESTED grand total ────────────────────────────── */}
                    {isNested && (
                      <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                        <TableCell sx={{ ...stickyCell, bgcolor: "#f1f5f9", fontWeight: 700, fontSize: 12, py: 1 }}>Grand Total</TableCell>
                        {outerCols.map((o) =>
                          innerCols.map((i) => (
                            <TableCell key={`${o}_${i}`} align="center" sx={{ fontWeight: 700, fontSize: 12, px: 1, py: 1, whiteSpace: "nowrap" }}>
                              {(outerInnerTotals[o]?.[i] || 0).toLocaleString()}
                            </TableCell>
                          ))
                        )}
                        <TableCell align="center" sx={{ fontWeight: 800, fontSize: 13, px: 1.5, py: 1, bgcolor: TEAL, color: "#fff", whiteSpace: "nowrap" }}>
                          {(filterDataSummary.totalContacts || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <Divider sx={{ mt: 5, mb: 2.5 }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="caption" color="text.disabled">
            Kestone Integrated Marketing Services
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Generated {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ShareStatsPage;
